<template>
  <q-dialog
    v-model="dialogOpen"
    persistent
    :maximized="$q.screen.lt.sm"
  >
    <q-card class="pos-dialog pos-dialog--sm column no-wrap post-sale-completion-dialog">
      <q-card-section class="dialog-header--emerald">
        <div>
          <div class="text-h6 text-white text-weight-light">{{ dialogTitle }}</div>
          <div class="text-caption text-white q-mt-xs">{{ dialogSubtitle }}</div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section class="pos-dialog__body col scroll">
        <div
          v-if="store.showCashChange"
          class="post-sale-cash-change-panel q-pa-md rounded-borders q-mb-md"
        >
          <div class="text-subtitle2 text-weight-medium q-mb-sm">
            {{ t('sales.postSaleCashChangeTitle') }}
          </div>
          <div
            v-if="showTipSummary"
            class="row items-center justify-between q-mb-md q-pa-sm rounded-borders bg-blue-1"
          >
            <div>
              <div class="text-subtitle2 text-weight-medium text-blue-10">{{ t('sales.postSaleTipHeading') }}</div>
              <div class="text-caption text-blue-10" style="opacity: 0.85">{{ t('sales.postSaleTipHint') }}</div>
            </div>
            <div class="text-h5 text-weight-bold text-primary">${{ formattedTipTotal }}</div>
          </div>
          <div class="row items-center justify-between q-mb-md">
            <div class="text-subtitle1 text-grey-8">{{ t('sales.saleReceiptAmountReceived') }}</div>
            <div class="text-h5 text-weight-bold">${{ formattedReceived }}</div>
          </div>
          <div class="row items-center justify-between">
            <div class="text-subtitle1 text-grey-8">{{ t('sales.saleReceiptChangeGiven') }}</div>
            <div class="text-h4 text-weight-bold text-primary">${{ formattedChange }}</div>
          </div>
          <div
            v-if="isExactTender"
            class="text-body2 text-grey-7 text-center q-mt-md"
          >
            {{ t('sales.postSaleCashChangeNoChangeDue') }}
          </div>
        </div>

        <template v-if="step === 'choices'">
          <q-separator v-if="store.showCashChange" class="q-mb-md" />

          <div class="q-gutter-sm">
            <div class="text-subtitle2 text-weight-medium q-mb-xs">
              {{ t('sales.postSaleReceiptTitle') }}
            </div>
            <div class="text-caption text-grey-7 q-mb-sm">
              {{ t('sales.postSaleReceiptSubtitle') }}
            </div>

            <div
              v-if="showTipSummary && !store.showCashChange"
              class="post-sale-tip-summary q-pa-md rounded-borders q-mb-sm"
            >
              <div class="row items-center no-wrap q-gutter-sm q-mb-sm">
                <q-icon name="volunteer_activism" color="primary" size="28px" />
                <div class="col min-width-0">
                  <div class="text-subtitle2 text-weight-medium">{{ t('sales.postSaleTipHeading') }}</div>
                  <div class="text-caption text-grey-7">{{ t('sales.postSaleTipHint') }}</div>
                </div>
              </div>
              <div class="row items-center justify-between">
                <div class="text-body2 text-grey-8">{{ t('sales.saleReceiptTip') }}</div>
                <div class="text-h5 text-weight-bold text-primary">${{ formattedTipTotal }}</div>
              </div>
            </div>

            <q-btn
              outline
              color="primary"
              class="full-width"
              icon="print"
              :label="t('sales.print')"
              :loading="processing"
              @click="choosePrint"
            />
            <q-btn
              outline
              color="primary"
              class="full-width"
              icon="email"
              :label="t('sales.email')"
              :disable="processing"
              @click="step = 'email'"
            />
            <q-btn
              outline
              color="primary"
              class="full-width"
              icon="sms"
              :label="t('sales.text')"
              :disable="processing"
              @click="openSmsStep"
            />
            <q-btn
              flat
              color="grey-8"
              class="full-width q-mt-sm"
              icon="receipt_long"
              :label="t('sales.postSaleReceiptView')"
              :disable="processing"
              @click="showPreview = true"
            />
          </div>
        </template>

        <div v-else-if="step === 'email'" class="q-gutter-md">
          <q-input
            v-model="emailValue"
            type="email"
            :label="t('sales.to')"
            dense
            outlined
            clearable
            :disable="processing"
          />
          <q-toggle
            v-if="store.customerId"
            v-model="updateCustomer"
            :label="t('sales.postSaleReceiptSaveToCustomer')"
            :disable="processing"
          />
          <q-toggle
            v-model="ccMe"
            :label="t('sales.ccMe')"
            :disable="processing"
          />
          <q-banner v-if="localError" dense class="bg-red-1 text-red-10 rounded-borders">
            {{ localError }}
          </q-banner>
        </div>

        <div v-else-if="step === 'sms'" class="q-gutter-md">
          <q-input
            v-model="phoneValue"
            :label="t('sales.to')"
            dense
            outlined
            clearable
            :disable="processing"
            @update:model-value="checkSmsOptOut"
          />
          <q-toggle
            v-if="store.customerId"
            v-model="updateCustomer"
            :label="t('sales.postSaleReceiptSaveToCustomer')"
            :disable="processing"
          />
          <q-banner v-if="smsOptedOut" dense class="bg-amber-1 text-amber-10 rounded-borders">
            {{ t('common.smsOptedOut') }}
          </q-banner>
          <q-banner v-if="localError" dense class="bg-red-1 text-red-10 rounded-borders">
            {{ localError }}
          </q-banner>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right" class="q-pa-md column q-gutter-sm">
        <q-btn
          v-if="step === 'choices'"
          color="primary"
          unelevated
          size="lg"
          class="full-width"
          :label="t('sales.postSaleCashChangeContinue')"
          :loading="processing"
          :disable="processing"
          @click="chooseContinue"
        />
        <div v-else class="row full-width justify-end q-gutter-sm">
          <q-btn
            flat
            :label="t('sales.cancel')"
            :disable="processing"
            @click="backToChoices"
          />
          <q-btn
            v-if="step === 'email'"
            color="primary"
            :label="t('sales.send')"
            :loading="processing"
            :disable="!emailValue?.trim()"
            @click="submitEmail"
          />
          <q-btn
            v-if="step === 'sms'"
            color="primary"
            :label="t('sales.send')"
            :loading="processing"
            :disable="!phoneValue?.trim() || smsOptedOut"
            @click="submitSms"
          />
        </div>
      </q-card-actions>
    </q-card>
  </q-dialog>

  <q-dialog v-model="showPreview" maximized>
    <q-card class="receipt-dialog-shell receipt-print-root column no-wrap fit">
      <q-card-section class="row items-center justify-between print-hide">
        <div class="text-h6">{{ t('sales.receipt') }}</div>
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>
      <q-separator class="print-hide" />
      <q-card-section v-if="previewSale" class="col scroll">
        <SaleReceipt :sale="previewSale" />
      </q-card-section>
      <q-card-actions align="right" class="print-hide">
        <q-btn flat :label="t('sales.close')" v-close-popup />
        <q-btn flat color="primary" :label="t('sales.print')" @click="choosePrintFromPreview" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SaleReceipt from '@/components/SaleReceipt.vue'
import { executePostSaleReceiptChoice, posService, smsService } from '@/services'
import { usePostSaleReceiptStore } from '@/stores/postSaleReceipt'
import { useBusinessStore } from '@/stores/business'
import { isUsPhoneNumber } from '@/utils/phone'

const { t } = useI18n()
const store = usePostSaleReceiptStore()
const businessStore = useBusinessStore()

const step = ref('choices')
const emailValue = ref('')
const phoneValue = ref('')
const updateCustomer = ref(true)
const ccMe = ref(false)
const processing = ref(false)
const localError = ref('')
const smsOptedOut = ref(false)
const showPreview = ref(false)
const previewSale = ref(null)

const showTipSummary = computed(() => Number(store.tipTotal || 0) > 0.005)
const formattedTipTotal = computed(() => Number(store.tipTotal || 0).toFixed(2))
const formattedReceived = computed(() => Number(store.amountReceived || 0).toFixed(2))
const formattedChange = computed(() => Number(store.changeGiven ?? 0).toFixed(2))
const isExactTender = computed(() => Math.abs(Number(store.changeGiven ?? 0)) < 0.005)

const dialogTitle = computed(() => (
  store.showCashChange ? t('sales.postSaleCompleteTitle') : t('sales.postSaleReceiptTitle')
))

const dialogSubtitle = computed(() => (
  store.showCashChange
    ? t('sales.postSaleCompleteSubtitle')
    : t('sales.postSaleReceiptSubtitle')
))

const dialogOpen = computed({
  get: () => store.showCashierChoices,
  set: () => {}
})

watch(
  () => store.showCashierChoices,
  (open) => {
    if (open) {
      step.value = 'choices'
      emailValue.value = store.prefilledEmail || ''
      phoneValue.value = store.prefilledPhone || ''
      updateCustomer.value = !!store.customerId
      localError.value = ''
      smsOptedOut.value = false
      ccMe.value = false
      processing.value = false
    }
  }
)

watch(
  () => store.pendingSaleId,
  () => {
    processing.value = false
    localError.value = ''
  }
)

watch(
  () => store.status,
  (status) => {
    if (status === 'pending' || status === 'done' || status === 'idle') {
      processing.value = false
    }
  }
)

watch(showPreview, async (open) => {
  if (!open) {
    previewSale.value = null
    return
  }
  if (!store.pendingSaleId) return
  try {
    previewSale.value = await posService.getSale(store.pendingSaleId)
    if (store.pendingSaleSnapshot?.invoice_payment_context) {
      previewSale.value = {
        ...previewSale.value,
        invoice_payment_context: store.pendingSaleSnapshot.invoice_payment_context
      }
    }
  } catch (e) {
    console.error(e)
    showPreview.value = false
  }
})

function backToChoices () {
  step.value = 'choices'
  localError.value = ''
}

async function checkSmsOptOut () {
  const phone = phoneValue.value?.trim()
  if (!phone) {
    smsOptedOut.value = false
    return
  }
  try {
    const { opted_out } = await smsService.getOptOutStatus(phone)
    smsOptedOut.value = !!opted_out
  } catch {
    smsOptedOut.value = false
  }
}

function openSmsStep () {
  step.value = 'sms'
  void checkSmsOptOut()
}

async function runChoice (args) {
  processing.value = true
  localError.value = ''
  try {
    await executePostSaleReceiptChoice({
      saleId: store.pendingSaleId,
      source: 'cashier',
      logoUrlFallback:
        businessStore.info?.logo_url ||
        businessStore.info?.logo_original_url ||
        null,
      ...args
    })
  } catch (e) {
    localError.value = e?.response?.data?.message || e?.message || t('sales.postSaleReceiptFailed')
  } finally {
    processing.value = false
  }
}

function choosePrint () {
  void runChoice({ choice: 'print' })
}

function choosePrintFromPreview () {
  showPreview.value = false
  choosePrint()
}

function chooseContinue () {
  void runChoice({ choice: 'none' })
}

function submitEmail () {
  void runChoice({
    choice: 'email',
    email: emailValue.value,
    updateCustomer: updateCustomer.value,
    ccMe: ccMe.value
  })
}

function submitSms () {
  if (!isUsPhoneNumber(phoneValue.value)) {
    localError.value = t('sales.postSaleReceiptInvalidPhone')
    return
  }
  void runChoice({
    choice: 'sms',
    phone: phoneValue.value,
    updateCustomer: updateCustomer.value
  })
}
</script>

<style scoped>
.post-sale-tip-summary {
  background: rgba(25, 118, 210, 0.08);
  border: 1px solid rgba(25, 118, 210, 0.18);
}

.post-sale-cash-change-panel {
  background: rgba(39, 174, 96, 0.08);
  border: 1px solid rgba(39, 174, 96, 0.18);
}
</style>
